import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTable, usePagination } from "react-table";
import HeaderMenu from "../Header/HeaderMenu";
import axiosApi from "../Utils/axiosApi";
import checkPermission from "../Utils/checkPermission";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Pagination,
  Chip,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";

const AttendanceList = () => {
  // ------------------- State -------------------
  const [attendanceList, setAttendanceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [hostelList, setHostelList] = useState([]);
  const [filters, setFilters] = useState({
    hostel: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ------------------- Fetch Hostels -------------------
  useEffect(() => {
    axiosApi.post("/hostel/getHostelName", {}).then((res) => {
      const list = Array.isArray(res.data)
        ? res.data
        : res.data.hostels || [];
      setHostelList(
        list.map((h) =>
          Array.isArray(h)
            ? { id: h[0], name: h[1] }
            : { id: h.id, name: h.name }
        )
      );
    });
  }, []);

  // ------------------- Fetch Attendance -------------------
  const fetchAttendance = useCallback(async (payload) => {
    setLoading(true);
    try {
      const res = await axiosApi.post(
        "/hostelerLeaveRequest/getHostelLeaveRequest",
        payload
      );
      setAttendanceList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setAttendanceList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ------------------- Search -------------------
  const handleSearch = useCallback(() => {
    if (!filters.hostel) return;

    const selected = hostelList.find((h) => h.name === filters.hostel);

    fetchAttendance({
      hostelIds: selected ? [selected.id] : [],
      leaveRequestStatusList: filters.status ? [filters.status] : null,
      leaveStartDate: filters.fromDate || null,
      leaveEndDate: filters.toDate || null,
    });
  }, [filters, hostelList, fetchAttendance]);

  // ------------------- Approve / Reject -------------------
  const handleUpdateStatus = useCallback(
    async (row, status) => {
      try {
        setActionLoading(true);
        await axiosApi.post(
          "/hostelerLeaveRequest/updateHostelLeaveRequestStatus",
          {
            hostelLeaveRequestIds: [row.original.id],
            leaveRequestStatus: status,
          }
        );
        handleSearch();
      } catch (err) {
        console.error(err);
      } finally {
        setActionLoading(false);
      }
    },
    [handleSearch]
  );

  // ------------------- Delete -------------------
  const handleDeleteClick = (row) => {
    setDeleteTarget(row.original);
    setDeleteOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;

    try {
      setActionLoading(true);
      await axiosApi.post(
        "/hostelerLeaveRequest/deleteHostelLeaveRequest",
        {
          hostelLeaveRequestIds: [deleteTarget.id],
        }
      );
      setDeleteOpen(false);
      setDeleteTarget(null);
      handleSearch();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  // ------------------- Status Chip -------------------
  const renderStatusChip = (status) => {
    if (status === "APPROVED")
      return <Chip label="Approved" color="success" size="small" />;
    if (status === "REJECTED")
      return <Chip label="Rejected" color="error" size="small" />;
    return <Chip label="Pending" color="warning" size="small" />;
  };

  // ------------------- Columns -------------------
  const columns = useMemo(
    () => [
	  { Header: '#', accessor: '' },
      { Header: "Hosteler Name", accessor: "hostelerName" },
      { Header: "Hostel Name", accessor: "hostelName" },
      { Header: "Start Date", accessor: "leaveStartDate" },
      { Header: "End Date", accessor: "leaveEndDate" },
      { Header: "Reason", accessor: "reason" },
      {
        Header: "Status",
        accessor: "leaveRequestStatus",
        Cell: ({ value }) => renderStatusChip(value),
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="actions-container">
            <Button
              size="small"
              variant="outlined"
              color="success"
              disabled={
                actionLoading ||
                !checkPermission(
                  "Hostel",
                  "Hosteler List",
                  "UPDATEABSENTSTATUSFIELD"
                )
              }
              onClick={() => handleUpdateStatus(row, "APPROVED")}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={
                actionLoading ||
                !checkPermission(
                  "Hostel",
                  "Hosteler List",
                  "UPDATEABSENTSTATUSFIELD"
                )
              }
              onClick={() => handleUpdateStatus(row, "REJECTED")}
            >
              Reject
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              disabled={
                actionLoading ||
                !checkPermission(
                  "Hostel",
                  "Hosteler List",
                  "DELETEABSENT"
                )
              }
              onClick={() => handleDeleteClick(row)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [actionLoading, handleUpdateStatus]
  );

  // ------------------- React Table -------------------
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    state: { pageIndex, pageSize },
    setPageSize,
    pageCount,
  } = useTable(
    {
      columns,
      data: attendanceList,
      initialState: { pageIndex: 0, pageSize: 5 },
    },
    usePagination
  );

  return (
    <>
      <HeaderMenu />

      <div className="user-list">
        <Typography variant="h5" sx={{ mb: 2 }}>
          Attendance List
        </Typography>

        {/* Filters */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                size="small"
                fullWidth
                label="Hostel"
                value={filters.hostel}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, hostel: e.target.value }))
                }
              >
                {hostelList.map((h) => (
                  <MenuItem key={h.id} value={h.name}>
                    {h.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                select
                size="small"
                fullWidth
                label="Status"
                value={filters.status}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, status: e.target.value }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="From"
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, fromDate: e.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="To"
                InputLabelProps={{ shrink: true }}
                value={filters.toDate}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, toDate: e.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Button fullWidth variant="contained" onClick={handleSearch}>
                Search
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1 }}>Loading attendance...</Typography>
          </Box>
        ) : (
          <div className="table-container">
            <table {...getTableProps()} className="table">
              <thead>
                {headerGroups.map((hg) => (
                  <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                    {hg.headers.map((col) => (
                      <th {...col.getHeaderProps()} key={col.id}>
                        {col.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={row.id}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.column.id}
                          data-label={cell.column.Header}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && attendanceList.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Pagination
              count={pageCount}
              page={pageIndex + 1}
              onChange={(e, value) => gotoPage(value - 1)}
              color="primary"
            />
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[5, 10, 15, 20].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Box>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Absence</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this absence from{" "}
            <strong>{deleteTarget?.leaveStartDate || "-"}</strong> to{" "}
            <strong>{deleteTarget?.leaveEndDate || "-"}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirmed}
            disabled={actionLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AttendanceList;
